import { Signal, computed, signal } from '@angular/core';

import { TableColumnMeta } from '@shared/models/table.models';

export interface ColumnPreferences {
  /** The columns shown, in the table's own order. */
  readonly displayed: Signal<string[]>;
  readonly isDefault: Signal<boolean>;
  toggle(column: string): void;
  restoreDefault(): void;
}

/**
 * Which columns a page shows, remembered per page on this computer (R16). Stored under the same
 * key as the old `abook-column-selector` (`abook-columns-for-<page>`), so pages that moved to the
 * table bar kept what their users had chosen. Unknown or removed columns in storage are dropped.
 */
export function createColumnPreferences(
  storageKey: string,
  available: readonly TableColumnMeta[],
  defaults: readonly string[],
): ColumnPreferences {
  const key = `abook-columns-for-${storageKey}`;
  const known = new Set(available.map((c) => c.value));
  const inTableOrder = (columns: Iterable<string>) => {
    const chosen = new Set(columns);
    return available.map((c) => c.value).filter((v) => chosen.has(v) && known.has(v));
  };

  const read = (): string[] => {
    try {
      const raw = localStorage.getItem(key);
      // The old store wrote `{ displayedColumns: [...] }`; accept that and a bare array.
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      const list = Array.isArray(parsed)
        ? parsed
        : (parsed as { displayedColumns?: unknown } | null)?.displayedColumns;
      if (Array.isArray(list)) {
        const columns = inTableOrder(list.filter((c): c is string => typeof c === 'string'));
        if (columns.length) return columns;
      }
    } catch {
      // Private window or blocked storage: fall back to the page's standard.
    }
    return inTableOrder(defaults);
  };

  const displayed = signal(read());
  const write = (columns: string[]) => {
    displayed.set(columns);
    try {
      localStorage.setItem(key, JSON.stringify({ displayedColumns: columns }));
    } catch {
      // Not remembered, but still shown.
    }
  };

  const defaultSet = inTableOrder(defaults);
  return {
    displayed: displayed.asReadonly(),
    isDefault: computed(() => displayed().join() === defaultSet.join()),
    toggle: (column) => {
      const current = displayed();
      const next = current.includes(column)
        ? current.filter((c) => c !== column)
        : [...current, column];
      // At least one column stays — an empty table has nothing to hold on to.
      if (next.length) write(inTableOrder(next));
    },
    restoreDefault: () => write(defaultSet),
  };
}
